import {
  BG_STYLES,
  BORDER_STYLES,
  Chip,
  CHIP_TYPES,
  COMMON_STYLES,
  CrossIcon,
  LazyImage,
  TEXT_STYLES,
  TYPOGRAPHY,
  VARIANTS_STYLES,
} from "@dehaat/dds";
import React, { useEffect, useRef, useState } from "react";
import {
  Image,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Pressable,
  SafeAreaView,
  ScrollView,
  StyleSheet,
  View,
} from "react-native";
import getUserUploadedImage from "../../apis/getUserUploadedImage";
import { selectPlantixUId } from "app/farmer360/store/app";
import { useAppSelector } from "app/hooks/reduxHooks";
import { FallbackIcon } from "../RoundImageWithIcon";
import { SCREEN_HEIGHT, SCREEN_WIDTH } from "app/utils/common";
import { useTranslation } from "react-i18next";

interface ImageCompareModalProps {
  setShowImgCompareModal: (show: boolean) => void;
  selectedDiseaseImg: string[] | null;
}

const ImageCompareModal = ({
  setShowImgCompareModal,
  selectedDiseaseImg,
}: ImageCompareModalProps) => {
  const { t } = useTranslation();
  const [apiUploadedImage, setApiUploadedImage] = useState<string | null>(null);
  const scrollRef = useRef<ScrollView>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const plantixId = useAppSelector(selectPlantixUId);

  useEffect(() => {
    const fetchImage = async () => {
      if (plantixId) {
        const imageUrl = await getUserUploadedImage(plantixId);
        if (imageUrl) {
          setApiUploadedImage(imageUrl);
        } else {
          console.error("No image found for the given Plantix ID");
        }
      }
    };
    fetchImage();
  }, [plantixId]);

  const handleScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const index = Math.round(event.nativeEvent.contentOffset.x / SCREEN_WIDTH);
    setActiveIndex(index);
  };

  return (
    <SafeAreaView
      style={[
        BG_STYLES.bgBlack,
        COMMON_STYLES.z1,
        COMMON_STYLES.flex1,
        COMMON_STYLES.absolute,
        { top: 0, left: 0, width: SCREEN_WIDTH },
      ]}
    >
      <Pressable
        onPress={() => setShowImgCompareModal(false)}
        style={[COMMON_STYLES.absolute, { top: 12, left: 14, zIndex: 10 }]}
      >
        <CrossIcon height={32} width={32} strokeColor="white" />
      </Pressable>

      <View style={[COMMON_STYLES.relative]}>
        <Image
          source={{ uri: apiUploadedImage || "" }}
          style={[{ width: SCREEN_WIDTH, height: SCREEN_HEIGHT / 2 }]}
          resizeMode="cover"
        />
        <View style={[COMMON_STYLES.absolute, { bottom: 16, right: 0 }]}>
          <Chip
            label={t("your_photo")}
            type={CHIP_TYPES.BADGE}
            containerStyle={[
              BG_STYLES.bgNeutral80,
              BORDER_STYLES.borderNeutral80,
            ]}
            labelStyle={[
              TEXT_STYLES.textWhite,
              VARIANTS_STYLES.MEDIUM,
              TYPOGRAPHY.TEXT1,
            ]}
          />
        </View>
      </View>

      {selectedDiseaseImg && selectedDiseaseImg?.length > 0 && (
        <>
          <ScrollView
            ref={scrollRef}
            horizontal
            pagingEnabled
            onScroll={handleScroll}
            scrollEventThrottle={16}
            showsHorizontalScrollIndicator={false}
            style={[{ height: SCREEN_HEIGHT / 2 }]}
          >
            {selectedDiseaseImg.map((image, index) => (
              <LazyImage
                key={index}
                imageUrl={image}
                fallBackIcon={
                  <FallbackIcon
                    text="disease_name"
                    containerStyle={styles.carouselImage}
                  />
                }
                imgStyles={styles.carouselImage}
              />
            ))}
          </ScrollView>

          <View style={[COMMON_STYLES.row, COMMON_STYLES.justifyCenter]}>
            {selectedDiseaseImg.map((_, index) => (
              <View
                key={index}
                style={[styles.dot, activeIndex === index && styles.activeDot]}
              />
            ))}
          </View>
        </>
      )}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  carouselImage: {
    width: SCREEN_WIDTH,
    height: SCREEN_HEIGHT / 2,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: "#888",
    marginHorizontal: 4,
  },
  activeDot: {
    ...BG_STYLES.bgWhite,
  },
});

export default ImageCompareModal;
